import { Box, useTheme, useMediaQuery } from '@mui/material';
import React, { useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import ArrowCircleButton from '@components/atoms/arrowCircleButton';
import CarouselStyle from './style';

export type CarouselItemPosition = 'left' | 'center' | 'right' | 'hidden' | 'hidden-left' | 'hidden-right';
export type CarouselAnimationMode = 'scale' | 'slide';

/** Responsive value type - supports breakpoints or single value */
export type ResponsiveValue<T> = T | {
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
};

/** Custom dimensions for track */
export type TrackDimensions = {
  height?: ResponsiveValue<string | number>;
};

/** Custom dimensions for items (scale mode) */
export type ScaleItemDimensions = {
  center?: { 
    width: ResponsiveValue<string | number>; 
    height: ResponsiveValue<string | number>; 
  };
  side?: { 
    width: ResponsiveValue<string | number>; 
    height: ResponsiveValue<string | number>; 
  };
  gap?: ResponsiveValue<string | number>;
};

/** Custom dimensions for items (slide mode) */
export type SlideItemDimensions = {
  width?: ResponsiveValue<string | number>;
  height?: ResponsiveValue<string | number>;
  gap?: ResponsiveValue<string | number>;
};

/** Custom styles for dot indicators */
export type DotStyles = {
  width?: ResponsiveValue<string | number>;
  height?: ResponsiveValue<string | number>;
  gap?: ResponsiveValue<string | number>;
  borderRadius?: ResponsiveValue<string | number>;
  backgroundColor?: ResponsiveValue<string>;
  activeColor?: ResponsiveValue<string>;
  marginTop?: ResponsiveValue<string | number>;
};

export type CarouselProps<T> = {
  items: T[];
  renderItem: (item: T, index: number, position: CarouselItemPosition) => ReactNode;
  keyExtractor: (item: T, index: number) => string;
  autoPlay?: boolean;
  autoPlaySpeed?: number;
  showDots?: boolean;
  /** Show both arrows (can be overridden by showLeftArrow/showRightArrow) */
  showArrows?: boolean;
  /** Show left arrow (overrides showArrows for left) */
  showLeftArrow?: boolean;
  /** Show right arrow (overrides showArrows for right) */
  showRightArrow?: boolean;
  minItemsForCarousel?: number;
  onItemClick?: (item: T, index: number) => void;
  className?: string;
  pauseOnInteraction?: boolean;
  /** 
   * Animation mode: 
   * - 'scale': center item larger with scaling, index[0] at center
   * - 'slide': infinite scroll style, index[0] at leftmost, all same size
   */
  animationMode?: CarouselAnimationMode;
  /** Custom track dimensions */
  trackDimensions?: TrackDimensions;
  /** Custom item dimensions for scale mode */
  scaleItemDimensions?: ScaleItemDimensions;
  /** Custom item dimensions for slide mode */
  slideItemDimensions?: SlideItemDimensions;
  /** Custom styles for dot indicators */
  dotStyles?: DotStyles;
};

function Carousel<T>({
  items,
  renderItem,
  keyExtractor,
  autoPlay = false,
  autoPlaySpeed = 5000,
  showDots = true,
  showArrows = true,
  showLeftArrow,
  showRightArrow,
  minItemsForCarousel = 3,
  onItemClick,
  className = '',
  pauseOnInteraction = true,
  animationMode = 'scale',
  trackDimensions,
  scaleItemDimensions,
  slideItemDimensions,
  dotStyles,
}: CarouselProps<T>) {
  // Determine which arrows to show (individual props override showArrows)
  const shouldShowLeftArrow = showLeftArrow ?? showArrows;
  const shouldShowRightArrow = showRightArrow ?? showArrows;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  
  // Touch swipe support
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);

  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const hasMultipleItems = items.length >= minItemsForCarousel;
  const isSlideMode = animationMode === 'slide' && hasMultipleItems;

  // Helper to convert dimension to CSS value
  const toCssValue = useCallback((value: string | number | undefined) => {
    if (value === undefined) return undefined;
    return typeof value === 'number' ? `${value}px` : value;
  }, []);

  // Helper to resolve responsive value based on current breakpoint
  const resolveResponsive = useCallback(<T,>(value: ResponsiveValue<T> | undefined): T | undefined => {
    if (value === undefined) return undefined;
    
    // If it's not an object with breakpoints, return as-is
    if (typeof value !== 'object' || value === null) {
      return value as T;
    }

    // Check if it's a responsive object (has breakpoint keys)
    const breakpointKeys = ['xs', 'sm', 'md', 'lg', 'xl'];
    const hasBreakpointKeys = breakpointKeys.some(key => key in value);
    
    if (!hasBreakpointKeys) {
      return value as T;
    }

    // Resolve based on current breakpoint (mobile-first: xl > lg > md > sm > xs)
    const responsiveValue = value as { xs?: T; sm?: T; md?: T; lg?: T; xl?: T };
    if (isXl && responsiveValue.xl !== undefined) return responsiveValue.xl;
    if (isLg && responsiveValue.lg !== undefined) return responsiveValue.lg;
    if (isMd && responsiveValue.md !== undefined) return responsiveValue.md;
    if (isSm && responsiveValue.sm !== undefined) return responsiveValue.sm;
    return responsiveValue.xs;
  }, [isXl, isLg, isMd, isSm]);

  // Helper to convert responsive dimension to CSS value
  const toResponsiveCssValue = useCallback((value: ResponsiveValue<string | number> | undefined) => {
    const resolved = resolveResponsive(value);
    return toCssValue(resolved);
  }, [resolveResponsive, toCssValue]);

  // Responsive item dimensions for slide mode (can be overridden by props)
  const slideItemConfig = useMemo(() => {
    // Default responsive values
    let defaultWidth: number;
    let defaultGap: number;
    if (isXl) { defaultWidth = 480; defaultGap = 50; }
    else if (isLg) { defaultWidth = 400; defaultGap = 40; }
    else if (isMd) { defaultWidth = 320; defaultGap = 30; }
    else if (isSm) { defaultWidth = 260; defaultGap = 20; }
    else { defaultWidth = 200; defaultGap = 12; }

    // If custom dimensions provided, resolve responsive values
    const resolvedWidth = resolveResponsive(slideItemDimensions?.width);
    const resolvedGap = resolveResponsive(slideItemDimensions?.gap);

    const width = resolvedWidth !== undefined 
      ? (typeof resolvedWidth === 'number' ? resolvedWidth : parseInt(resolvedWidth as string, 10))
      : defaultWidth;
    const gap = resolvedGap !== undefined 
      ? (typeof resolvedGap === 'number' ? resolvedGap : parseInt(resolvedGap as string, 10))
      : defaultGap;

    return { width, gap };
  }, [isXl, isLg, isMd, isSm, slideItemDimensions, resolveResponsive]);

  // For slide mode: triple items for seamless infinite loop
  // Structure: [...items, ...items, ...items]
  // Start rendering from middle set so we can scroll both left and right
  const extendedItems = useMemo(() => {
    if (!isSlideMode) return items;
    return [...items, ...items, ...items];
  }, [items, isSlideMode]);

  // Middle set starts at items.length
  const middleSetStart = items.length;

  const handleNext = useCallback(() => {
    if (isSlideMode) {
      setIsTransitioning(true);
      setActiveIndex((prev) => prev + 1);
    } else {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }
  }, [items.length, isSlideMode]);

  const handlePrev = useCallback(() => {
    if (isSlideMode) {
      setIsTransitioning(true);
      setActiveIndex((prev) => prev - 1);
    } else {
      setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
    }
  }, [items.length, isSlideMode]);

  // Handle infinite loop jump for slide mode
  useEffect(() => {
    if (!isSlideMode) return;

    const totalItems = items.length;
    
    // If scrolled past the end of middle set, jump to beginning
    if (activeIndex >= totalItems) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex(0);
      }, 500);
      return () => clearTimeout(timer);
    }
    
    // If scrolled before the start of middle set, jump to end
    if (activeIndex < 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex(totalItems - 1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, items.length, isSlideMode]);

  // Re-enable transition after jump
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // Auto-play infinite loop
  useEffect(() => {
    if (!autoPlay || !hasMultipleItems || isPaused) return;

    const interval = setInterval(() => {
      handleNext();
    }, autoPlaySpeed);

    return () => clearInterval(interval);
  }, [autoPlay, hasMultipleItems, isPaused, autoPlaySpeed, handleNext]);

  // Get position class for scale mode
  const getPositionClass = useCallback((index: number): CarouselItemPosition => {
    if (!hasMultipleItems) return 'center';

    const totalItems = items.length;
    let offset = index - activeIndex;

    // Handle circular offset
    if (offset > totalItems / 2) offset -= totalItems;
    if (offset < -totalItems / 2) offset += totalItems;

    if (offset === 0) return 'center';
    if (offset === -1) return 'left';
    if (offset === 1) return 'right';
    if (offset < -1) return 'hidden-left';
    if (offset > 1) return 'hidden-right';
    
    return 'hidden';
  }, [hasMultipleItems, items.length, activeIndex]);

  const handleMouseEnter = () => {
    if (pauseOnInteraction) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnInteraction) {
      setIsPaused(false);
    }
  };

  const handleItemClick = (item: T, originalIndex: number) => {
    if (onItemClick) {
      onItemClick(item, originalIndex);
    }
  };

  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!hasMultipleItems) return;
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    isSwiping.current = true;
    
    if (pauseOnInteraction) {
      setIsPaused(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping.current) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current || !hasMultipleItems) return;
    
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance to trigger swipe
    
    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left -> go to next
        handleNext();
      } else {
        // Swiped right -> go to prev
        handlePrev();
      }
    }
    
    isSwiping.current = false;
    
    if (pauseOnInteraction) {
      setIsPaused(false);
    }
  };

  if (items.length === 0) return null;

  const carouselClasses = [
    hasMultipleItems ? 'has-multiple-items' : '',
    isSlideMode ? 'slide-mode' : 'scale-mode',
    className,
  ].filter(Boolean).join(' ');

  // Calculate translateX for slide mode
  // index[0] starts at left, scrolling moves items left (showing more from right)
  const getSlideTransform = () => {
    if (!isSlideMode) return undefined;
    
    const { width, gap } = slideItemConfig;
    const itemTotalWidth = width + gap;
    
    // Current scroll position: middleSetStart + activeIndex
    // This puts the active item at the leftmost position
    const currentIndex = middleSetStart + activeIndex;
    const translateX = currentIndex * itemTotalWidth;
    
    return `translateX(-${translateX}px) translateY(-50%)`;
  };

  // Get actual index in original items array
  const getOriginalIndex = (extendedIndex: number) => {
    return ((extendedIndex % items.length) + items.length) % items.length;
  };

  // Check if item is the first visible (leftmost) item
  const isFirstVisible = (extendedIndex: number) => {
    return extendedIndex === middleSetStart + activeIndex;
  };

  return (
    <CarouselStyle
      className={carouselClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Box 
        className="carousel-wrapper"
        style={{
          height: toResponsiveCssValue(trackDimensions?.height),
        }}
      >
        {/* Arrows at wrapper level */}
        {shouldShowLeftArrow && hasMultipleItems && (
          <Box className="arrow-left">
            <ArrowCircleButton direction="left" onClick={handlePrev} />
          </Box>
        )}

        {isSlideMode ? (
          // Slide mode: items start from left, infinite scroll
          <Box className="carousel-track-container" style={{
            height: toResponsiveCssValue(trackDimensions?.height),
          }}>
            <Box 
              ref={trackRef}
              className={`carousel-track-slide ${isTransitioning ? 'transitioning' : ''}`}
              style={{ 
                transform: getSlideTransform(),
                gap: toResponsiveCssValue(slideItemDimensions?.gap),
              }}
            >
              {extendedItems.map((item, extendedIndex) => {
                const originalIndex = getOriginalIndex(extendedIndex);
                const isFirst = isFirstVisible(extendedIndex);
                
                return (
                  <Box
                    key={`${keyExtractor(item, originalIndex)}-${extendedIndex}`}
                    className={`carousel-card-slide ${isFirst ? 'first-visible' : ''}`}
                    style={{
                      width: toResponsiveCssValue(slideItemDimensions?.width),
                      height: toResponsiveCssValue(slideItemDimensions?.height),
                    }}
                    onClick={() => handleItemClick(item, originalIndex)}
                  >
                    {renderItem(item, originalIndex, 'center')}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          // Scale mode: use absolute positioning with center zoom
          <Box className="carousel-track" style={{
            height: toResponsiveCssValue(trackDimensions?.height),
          }}>
            {items.map((item, index) => {
              const position = getPositionClass(index);
              const isCenter = position === 'center';
              const isSide = position === 'left' || position === 'right';
              
              // Get custom dimensions based on position (with responsive support)
              const customWidth = isCenter 
                ? toResponsiveCssValue(scaleItemDimensions?.center?.width)
                : isSide ? toResponsiveCssValue(scaleItemDimensions?.side?.width) : undefined;
              const customHeight = isCenter 
                ? toResponsiveCssValue(scaleItemDimensions?.center?.height)
                : isSide ? toResponsiveCssValue(scaleItemDimensions?.side?.height) : undefined;
              
              // Calculate translateX for scale mode positioning (gap between items)
              const getScaleTransform = () => {
                if (!scaleItemDimensions?.center?.width || !scaleItemDimensions?.side?.width) {
                  return undefined; // Use CSS default
                }
                
                const centerWidth = resolveResponsive(scaleItemDimensions.center.width);
                const sideWidth = resolveResponsive(scaleItemDimensions.side.width);
                const gap = resolveResponsive(scaleItemDimensions.gap) || 24; // default 24px
                
                if (centerWidth === undefined || sideWidth === undefined) return undefined;
                
                const centerWidthNum = typeof centerWidth === 'number' ? centerWidth : parseFloat(centerWidth);
                const sideWidthNum = typeof sideWidth === 'number' ? sideWidth : parseFloat(sideWidth);
                const gapNum = typeof gap === 'number' ? gap : parseFloat(gap);
                
                const offset = (centerWidthNum / 2) + (sideWidthNum / 2) + gapNum;
                
                if (position === 'left') return `translateX(-${offset}px)`;
                if (position === 'right') return `translateX(${offset}px)`;
                return 'translateX(0)';
              };
              
              return (
                <Box
                  key={keyExtractor(item, index)}
                  className={`carousel-card ${position}`}
                  style={{
                    width: customWidth,
                    height: customHeight,
                    transform: getScaleTransform(),
                  }}
                  onClick={() => handleItemClick(item, index)}
                >
                  {renderItem(item, index, position)}
                </Box>
              );
            })}
          </Box>
        )}

        {shouldShowRightArrow && hasMultipleItems && (
          <Box className="arrow-right">
            <ArrowCircleButton direction="right" onClick={handleNext} />
          </Box>
        )}
      </Box>

      {/* Dot indicators */}
      {showDots && hasMultipleItems && (
        <Box 
          className="dot-indicators"
          style={{
            gap: toResponsiveCssValue(dotStyles?.gap),
            marginTop: toResponsiveCssValue(dotStyles?.marginTop),
          }}
        >
          {items.map((_, index) => {
            const isActive = isSlideMode 
              ? ((activeIndex % items.length) + items.length) % items.length === index
              : activeIndex === index;
            return (
              <Box
                key={index}
                className={`dot ${isActive ? 'active' : ''}`}
                style={{
                  width: toResponsiveCssValue(dotStyles?.width),
                  height: toResponsiveCssValue(dotStyles?.height),
                  borderRadius: toResponsiveCssValue(dotStyles?.borderRadius),
                  backgroundColor: isActive 
                    ? resolveResponsive(dotStyles?.activeColor) 
                    : resolveResponsive(dotStyles?.backgroundColor),
                }}
                onClick={() => {
                  if (isSlideMode) {
                    setIsTransitioning(true);
                    setActiveIndex(index);
                  } else {
                    setActiveIndex(index);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
    </CarouselStyle>
  );
}

export default Carousel;
